import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Switch } from "@xola/ui-kit";
import { getFields, initializeFieldResponse, isAutoGeneratedName, isMarketingConsent, SecondaryButton } from "common";
import { findIndex, isEmpty, isEqual, omit, set, sortBy } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { Field, FieldType, Form, Guest, Traveler } from "types";
import { getMarketingConsentFields, isQuestionnaireSkipped } from "../../../models/Form/Form";
import {
    applySharedAddonOverrides,
    getFieldValue,
    getPrimaryGuest,
    hasAddonFieldsInGuest,
    isOrganizerSelected,
    isPrimaryGuest,
} from "../../../models/Guest";
import { useCreatePurchaseStore } from "../../../stores/createPurchaseStore";
import {
    handleGuestListChange,
    handleIgnoreQuestionnaireChange,
    handlePackageGuestListChange,
} from "../helpers/PurchaseCreateHelpers";
import { createPurchaseStoreToPayload, updatePurchaseData, useUpdatePurchase } from "../PurchaseManager";
import { useCustomerDetailsStore } from "../store/customerDetailsStore";
import { GuestQuestionnaireItem } from "./GuestQuestionnaireItem";
import { useVisibility } from "@/hooks/useVisibility";
import { getPurchaseItemGuests, isMembershipItem } from "@/models/PurchaseItem";

export const GuestQuestionnaireList = React.memo(() => {
    const { mutateAsync: updatePurchase } = useUpdatePurchase();

    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
    const form = useCreatePurchaseStore((state) => state.getForm());
    const formResponse = useCreatePurchaseStore((state) => state.getActiveItem().formResponse);
    const guestsFromStore = useCreatePurchaseStore((state) => state.getActiveItem().guests);
    const disableRequiredFormFields = useCreatePurchaseStore(
        (state) => state.getActiveItem().disableRequiredFormFields,
    );

    const guests = useMemo(
        () => getPurchaseItemGuests(guestsFromStore, form, formResponse),
        [guestsFromStore, form, formResponse],
    );

    const isMembership = useCreatePurchaseStore((state) => isMembershipItem(state.getActiveItem()));
    const [animationParent] = useAutoAnimate<HTMLDivElement>();
    const isPurchaserOrganizer = useCreatePurchaseStore((state) => state.isPurchaserOrganizer);
    const product = useCreatePurchaseStore((state) => state.getActiveProduct());

    const isSharedPackage = useCreatePurchaseStore(
        (state) => !!state.getPurchasePackageId() && !state.isVariableDemographicsPackage(),
    );
    const [initialGuests, setInitialGuests] = useState(guests);

    const organizer = useCreatePurchaseStore((state) => state.organizer);

    const [showAllAttendees, { toggle: toggleShowAllAttendees, hide: hideAllAttendees }] = useVisibility(false);
    const onChangeCallback = isSharedPackage ? handlePackageGuestListChange : handleGuestListChange;

    const handleGuestChange = (guestId: string) => async (updatedGuest: Guest, skipPurchaseUpdate?: boolean) => {
        /** Check if the complete guest object needs to updated or just a few properties. In Questionnaire form, it is possible user is only updating `name` and not any other properties  */
        const isPartialUpdate = !("formResponse" in updatedGuest);

        const hasAddonFields = hasAddonFieldsInGuest(updatedGuest);
        const updatedGuests = guests.map((guest) => {
            if (guest.id !== guestId) {
                return guest;
            }

            // If only Guest Name is modified, omit passing old name in FormResponse
            let currentGuest = { ...guest };
            if (guest.formResponse) {
                currentGuest = { ...guest, formResponse: omit(guest.formResponse, "name") };
            }

            return isPartialUpdate ? { ...currentGuest, ...updatedGuest } : updatedGuest;
        });

        onChangeCallback(isPartialUpdate ? updatedGuests : applySharedAddonOverrides(updatedGuests, updatedGuest));

        const haveFieldForPaymentSummaryUpdate = updatedGuest.formResponse?.fields?.some((field) => {
            return field.type === FieldType.PICKUP;
        });

        // Should be triggered if we want to update payment summary after questionnaires change
        if (!isPartialUpdate && !skipPurchaseUpdate && haveFieldForPaymentSummaryUpdate) {
            const payload = createPurchaseStoreToPayload(useCreatePurchaseStore.getState().purchase);
            useCreatePurchaseStore.setState({ isProcessing: true });
            const newPurchase = await updatePurchase(payload);
            updatePurchaseData(newPurchase);
        }

        if (hasAddonFields) {
            useCreatePurchaseStore.setState((state) => ({
                rerunTrigger: state.rerunTrigger + 1,
            }));
        }
    };

    const getFormResponse = (
        guest: Guest,
        isPrimary: boolean,
        isPurchaserOrganizer?: boolean,
        updateAllFields?: boolean,
        resetAllFields?: boolean,
        fieldsToUpdate?: Array<{ type: FieldType; value: any }>,
    ) => {
        if (!form) {
            return undefined;
        }

        // if guest doesn't have any formResponse filled don't pass empty formResponse data
        const formResponse = initializeFormResponse(form, isPrimary, guest, organizer);
        if (!formResponse) {
            return undefined;
        }

        if ((updateAllFields || resetAllFields) && isPurchaserOrganizer) {
            for (const field of getFields(form, isPrimary)) {
                const index = findIndex(formResponse.fields, { type: field.type });
                const value = updateAllFields && !resetAllFields ? getFieldValue(field, guest) : "";

                if (index !== -1) {
                    set(formResponse.fields[index], "value", value);
                }
            }
        }

        if (fieldsToUpdate && isPurchaserOrganizer) {
            for (const update of fieldsToUpdate) {
                const index = findIndex(formResponse.fields, { type: update.type });
                if (index !== -1) {
                    set(formResponse.fields[index], "value", update.value);
                }
            }
        }

        return formResponse;
    };

    const shouldToggleIgnoreValidations = disableRequiredFormFields === undefined && isQuestionnaireSkipped(form);

    useEffect(() => {
        // If optionalSourcesList contains "xola", we need to toggle on the "Ignore questionnaire validations" by
        // default
        if (shouldToggleIgnoreValidations) {
            handleIgnoreQuestionnaireChange(true);
        }
    }, [shouldToggleIgnoreValidations]);

    const hasOptInFieldsForAll = useMemo(() => {
        if (!form?.fields || guests.length <= 1) {
            return false;
        }

        const marketingConsentFields = getMarketingConsentFields(form);
        const askAllGuests = marketingConsentFields.some((field) => !field.organizerOnly);

        return marketingConsentFields.length > 0 && askAllGuests;
    }, [form, guests.length]);

    const hasProcessedGuests = isOrganizerSelected(guests) && !hasOptInFieldsForAll;

    useEffect(() => {
        if (hasProcessedGuests) {
            return;
        }

        const { purchase } = useCreatePurchaseStore.getState();
        const item = useCreatePurchaseStore.getState().getActiveItem();
        const updatedGuests = guests.map((guest) => {
            const isPrimary = isPrimaryGuest(guest, purchase, item);

            if (guest.formResponse) {
                return {
                    ...guest,
                    primary: isPrimary,
                };
            }

            if (!isPrimary && !hasOptInFieldsForAll) {
                // This a non-primary guest, and we don't need to process because there are no opt-in fields
                return guest;
            }

            return {
                ...guest,
                primary: isPrimary,
                formResponse: form ? initializeFormResponse(form, isPrimary, guest, organizer) : undefined,
            };
        });

        if (isEqual(guests, updatedGuests)) {
            return;
        }

        handleGuestListChange(updatedGuests);
    }, [guests, hasProcessedGuests, form, organizer, hasOptInFieldsForAll]);

    useEffect(() => {
        hideAllAttendees();
    }, [activeItemIndex, hideAllAttendees]);

    const handleMainGuestChange = (newPrimaryGuest: Guest) => {
        const previousMainGuest = getPrimaryGuest(guests);
        const { customerName, phone, customerEmail } = useCustomerDetailsStore.getState();
        const updatedGuests = guests.map((guest) => {
            if (newPrimaryGuest.id === guest.id && previousMainGuest) {
                return {
                    ...guest,
                    name: isPurchaserOrganizer && !isAutoGeneratedName(customerName) ? customerName : guest.name,
                    primary: true,
                    organizer: true,
                    formResponse: getFormResponse(previousMainGuest, true, isPurchaserOrganizer, true, false, [
                        { type: FieldType.EMAIL, value: customerEmail },
                        { type: FieldType.PHONE, value: phone },
                    ]),
                };
            }

            if (previousMainGuest?.id === guest.id) {
                const initialGuest = initialGuests.find((initialGuest) => initialGuest.id === previousMainGuest.id);

                return {
                    ...guest,
                    name: initialGuest?.name ?? "",
                    primary: false,
                    organizer: false,
                    formResponse: getFormResponse(previousMainGuest, false, isPurchaserOrganizer, false, true, [
                        { type: FieldType.EMAIL, value: "" },
                        { type: FieldType.PHONE, value: "" },
                    ]),
                };
            }

            return guest;
        });

        handleGuestListChange(updatedGuests);
    };

    useEffect(() => {
        if (initialGuests) {
            const newGuest = guests.find((guest) => {
                return !initialGuests.some((initialGuest) => guest.id === initialGuest.id);
            });

            if (newGuest) {
                setInitialGuests((previous) => [...previous, newGuest]);
            }
        }
    }, [initialGuests, guests]);

    return (
        <div>
            <div className="mb-4 flex items-center">
                <h3 className="mr-4">{isMembership ? "Member" : "Attendee"} Info</h3>
                {form && !isEmpty(form.fields) && (
                    <Switch.Group>
                        <Switch
                            isChecked={disableRequiredFormFields}
                            size="small"
                            data-testid="guest-questionnaire-list-disable-required-fields-checkbox"
                            onChange={handleIgnoreQuestionnaireChange}
                        />

                        <Switch.Label className="ml-2">Ignore questionnaire for now</Switch.Label>
                    </Switch.Group>
                )}
            </div>
            {product ? (
                <div ref={animationParent} className="space-y-3">
                    {sortBy(guests, "sequence")
                        .slice(0, showAllAttendees ? guests.length : 5)
                        .map((guest) => (
                            <GuestQuestionnaireItem
                                key={guest.id}
                                form={form}
                                product={product}
                                guest={form?.organizerOnly ? { ...guest, formResponse: formResponse } : guest}
                                mainGuestVisible={!isMembership}
                                disableRequiredFields={disableRequiredFormFields ?? false}
                                onChange={handleGuestChange(guest.id)}
                                onMainGuestChange={handleMainGuestChange}
                            />
                        ))}

                    {guests.length > 5 && (
                        <SecondaryButton onClick={toggleShowAllAttendees}>
                            {showAllAttendees ? "Show Less" : "Show All"}
                        </SecondaryButton>
                    )}
                </div>
            ) : null}

            <hr className="!mt-4 !mb-8 text-gray-light" />
        </div>
    );
});

GuestQuestionnaireList.displayName = "GuestQuestionnaireList";

export const initializeFormResponse = (form: Form, isPrimary: boolean, guest: Guest, organizer?: Traveler) => {
    if (!form) {
        return undefined;
    }

    return {
        fields: getFields(form, isPrimary).map((field) => {
            return { ...initializeFieldResponse(field), value: getFieldData(isPrimary, field, guest, organizer) };
        }),
    };
};

const getFieldData = (isPrimary: boolean, field: Field, guest: Guest, organizer?: Traveler) => {
    const defaultValue = getFieldValue(field, guest);
    if (isEmpty(field.label)) {
        return defaultValue;
    }

    // For opt-in-by default consent fields, we initialize the value to "Yes"
    if (isMarketingConsent(field) && field.optInByDefault) {
        return isEmpty(defaultValue) ? "Yes" : defaultValue;
    }

    if (!isPrimary || !organizer?.data) {
        return defaultValue;
    }

    if (field.label === "Email") {
        return organizer.email;
    }

    return organizer.data[field.label as keyof typeof organizer.data] ?? defaultValue;
};
